package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.state.SHOW_REVAMP
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminOverviewInsightsResponse
import twist.mena.sports.shared.dto.common.HealthResponse

private const val DASHBOARD_VERSION = "0.4.0"

@Composable
fun OverviewScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    val baseUrl = js("window.ADMIN_API_BASE_URL || ''") as String
    var health by remember { mutableStateOf<HealthResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var insightsState by remember { mutableStateOf<UiState<AdminOverviewInsightsResponse>>(UiState.Loading) }

    LaunchedEffect(Unit) {
        try {
            health = apiClient.getHealth()
        } catch (e: Exception) {
            error = e.message
        } finally {
            loading = false
        }
        try {
            insightsState = UiState.Success(apiClient.getOverviewInsights())
        } catch (e: Exception) {
            insightsState = UiState.Error("Failed to load insights")
        }
    }

    // Content insights
    when (val s = insightsState) {
        is UiState.Loading -> {}
        is UiState.Error -> {}
        is UiState.Success -> {
            val data = s.data

            if (SHOW_REVAMP) {
                // Content stats strip
                SectionTitle("Content Overview (Revamp)", colors)
                Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.lg); property("flex-wrap", "wrap") } }) {
                    OverviewStatCard(colors, "Teams", data.totalTeams.toString(), colors.primary)
                    OverviewStatCard(colors, "Leagues", data.totalLeagues.toString(), colors.info)
                    OverviewStatCard(colors, "Players", data.totalPlayers.toString(), colors.success)
                    OverviewStatCard(colors, "Fixtures", data.totalFixtures.toString(), colors.warning)
                }
            }

            // Football (legacy data) content stats
            if (data.totalLegacyTeams > 0 || data.totalLegacyLeagues > 0 || data.totalLegacyPlayers > 0) {
                SectionTitle("Content Overview", colors)
                Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.lg); property("flex-wrap", "wrap") } }) {
                    OverviewStatCard(colors, "Teams", data.totalLegacyTeams.toString(), colors.primary)
                    OverviewStatCard(colors, "Leagues", data.totalLegacyLeagues.toString(), colors.info)
                    OverviewStatCard(colors, "Players", data.totalLegacyPlayers.toString(), colors.success)
                }
            }

            if (SHOW_REVAMP) {
                // Fixture status breakdown
                if (data.fixturesByStatus.isNotEmpty()) {
                    SectionTitle("Fixture Status", colors)
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.lg); property("flex-wrap", "wrap") } }) {
                        data.fixturesByStatus.entries.sortedByDescending { it.value }.forEach { (status, count) ->
                            val statusColor = when (status) {
                                "FT", "AET", "PEN" -> colors.success
                                "NS", "TBD" -> colors.textMuted
                                "1H", "2H", "HT", "ET", "BT", "P", "LIVE" -> colors.warning
                                "PST", "CANC", "ABD", "SUSP", "INT" -> colors.danger
                                else -> colors.textMuted
                            }
                            OverviewStatCard(colors, status, count.toString(), statusColor)
                        }
                    }
                }

                // Sync health
                if (data.recentSyncs.isNotEmpty()) {
                    SectionTitle("Sync Health", colors)
                    Div({
                        style {
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.lg)
                            property("border", "1px solid ${colors.border}")
                            marginBottom(Spacing.xxl)
                            property("overflow", "hidden")
                        }
                    }) {
                        // Table header
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "1fr 100px 120px 1fr")
                                gap(Spacing.md)
                                padding(Spacing.md, Spacing.lg)
                                backgroundColor(Color(colors.background))
                                fontWeight("600")
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Span { Text("Job") }
                            Span { Text("Status") }
                            Span { Text("Records") }
                            Span { Text("Finished") }
                        }
                        data.recentSyncs.forEach { sync ->
                            Div({
                                style {
                                    display(DisplayStyle.Grid)
                                    property("grid-template-columns", "1fr 100px 120px 1fr")
                                    gap(Spacing.md)
                                    padding(Spacing.md, Spacing.lg)
                                    property("border-top", "1px solid ${colors.border}")
                                    fontSize(Typography.sm)
                                }
                            }) {
                                Span({ style { color(Color(colors.text)) } }) { Text(sync.jobName) }
                                Span({
                                    style {
                                        color(Color(if (sync.status == "success") colors.success else if (sync.status == "running") colors.warning else colors.danger))
                                        fontWeight("600")
                                    }
                                }) { Text(sync.status) }
                                Span({ style { color(Color(colors.text)) } }) { Text(sync.recordsUpdated.toString()) }
                                Span({ style { color(Color(colors.textMuted)); fontSize(Typography.xs) } }) {
                                    Text(sync.finishedAtUtc ?: "in progress")
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // API Status card
    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("border", "1px solid ${colors.border}")
            maxWidth(600.px)
            themeTransition()
        }
    }) {
        H2({
            style {
                marginTop(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.lg)
                color(Color(colors.textMuted))
            }
        }) { Text("System Status") }

        if (loading) {
            Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        } else if (error != null) {
            Div({ style { color(Color(colors.danger)) } }) { Text("Error: $error") }
        } else if (health != null) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "auto 1fr")
                    property("gap", "${Spacing.sm} ${Spacing.lg}")
                }
            }) {
                StatusRow("Dashboard", "v$DASHBOARD_VERSION", colors.textMuted, colors)
                StatusRow("API", health!!.status, if (health!!.status == "up") colors.success else colors.danger, colors)
                health!!.checks?.forEach { (name, status) ->
                    StatusRow(
                        name.replaceFirstChar { it.uppercase() },
                        status,
                        if (status == "up") colors.success else colors.danger,
                        colors
                    )
                }
            }
        }

        Button({
            onClick {
                scope.launch {
                    loading = true
                    error = null
                    try {
                        health = apiClient.getHealth()
                    } catch (e: Exception) {
                        error = e.message
                    } finally {
                        loading = false
                    }
                }
            }
            style {
                marginTop(Spacing.lg)
                padding(Spacing.sm, Spacing.lg)
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                borderRadius(BorderRadius.md)
                property("border", "1px solid ${colors.border}")
                cursor("pointer")
                fontSize(Typography.base)
                themeTransition()
            }
        }) { Text("Refresh") }
    }
}

@Composable
private fun SectionTitle(title: String, colors: AppColors) {
    H2({
        style {
            marginTop(0.px)
            marginBottom(Spacing.md)
            fontSize(Typography.lg)
            color(Color(colors.textMuted))
            fontWeight("600")
        }
    }) { Text(title) }
}

@Composable
private fun OverviewStatCard(colors: AppColors, label: String, value: String, accentColor: String) {
    Div({
        style {
            padding(Spacing.sm, Spacing.md)
            backgroundColor(Color(colors.surface))
            borderRadius(Spacing.xs)
            property("border", "1px solid ${colors.border}")
            property("border-left", "3px solid $accentColor")
            property("min-width", "100px"); property("flex", "1")
        }
    }) {
        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(label) }
        Div({ style { fontSize(Typography.lg); fontWeight("700"); color(Color(colors.text)) } }) { Text(value) }
    }
}

@Composable
private fun StatusRow(label: String, value: String, valueColor: String, colors: AppColors) {
    Span({
        style {
            fontWeight("600")
            color(Color(colors.textMuted))
        }
    }) { Text("$label:") }
    Span({ style { color(Color(valueColor)) } }) { Text(value) }
}
